// components/MarkdownQuillEditor.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import TurndownService from 'turndown';
import Showdown from 'showdown';
import 'react-quill/dist/quill.snow.css';

// ============================================
// CONVERTERS
// ============================================

// Markdown -> HTML (for loading into Quill)
const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  const converter = new Showdown.Converter({
    tables: true,
    strikethrough: true,
    tasklists: true,
    simpleLineBreaks: true,
  });
  
  return converter.makeHtml(markdown);
};

// HTML -> Markdown (for saving)
const htmlToMarkdown = (html) => {
  if (!html || html === '<p><br></p>' || html === '<p></p>') return '';
  
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
  });

  // Custom rule for strikethrough
  turndownService.addRule('strikethrough', {
    filter: ['del', 's', 'strike'],
    replacement: (content) => `~~${content}~~`,
  });

  // Custom rule for line breaks
  turndownService.addRule('lineBreak', {
    filter: 'br',
    replacement: () => '\n',
  });

  return turndownService.turndown(html);
};

// ============================================
// COMPONENT
// ============================================

const MarkdownQuillEditor = ({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  rows = 6,
  className = '',
  debounceMs = 300,
  readOnly = false,
}) => {
  const quillRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const timeoutRef = useRef(null);
  const internalValueRef = useRef(value);

  // Calculate height based on rows
  const editorHeight = rows * 24;

  // Initialize editor with markdown content
  useEffect(() => {
    if (quillRef.current && !initialized) {
      const editor = quillRef.current.getEditor();
      const html = markdownToHtml(value);
      editor.clipboard.dangerouslyPasteHTML(html);
      internalValueRef.current = value;
      setInitialized(true);
    }
  }, [value, initialized]);

  // Handle external value changes (e.g., form reset)
  useEffect(() => {
    if (initialized && value !== internalValueRef.current && quillRef.current) {
      const editor = quillRef.current.getEditor();
      const html = markdownToHtml(value);
      
      // Save cursor position
      const selection = editor.getSelection();
      
      // Update content
      editor.clipboard.dangerouslyPasteHTML(html);
      
      // Restore cursor if possible
      if (selection) {
        try {
          editor.setSelection(selection);
        } catch (e) {
          // Ignore if selection is invalid
        }
      }
      
      internalValueRef.current = value;
    }
  }, [value, initialized]);

  // Handle changes - convert to markdown and call onChange
  const handleChange = useCallback(
    (content, delta, source, editor) => {
      // Only process user changes
      if (source !== 'user') return;

      // Clear previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Debounce the conversion
      timeoutRef.current = setTimeout(() => {
        const html = editor.getHTML();
        const markdown = htmlToMarkdown(html);
        
        internalValueRef.current = markdown;
        
        if (onChange) {
          onChange(markdown);
        }
      }, debounceMs);
    },
    [onChange, debounceMs]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Quill toolbar configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  // Allowed formats
  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'link',
  ];

  return (
    <div className={`markdown-editor-wrapper ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        onChange={handleChange}
        readOnly={readOnly}
      />
      <style>{`
        .markdown-editor-wrapper .ql-toolbar {
          border: 1px solid #d1d5db;
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          background: #f9fafb;
        }
        .markdown-editor-wrapper .ql-container {
          border: 1px solid #d1d5db;
          border-radius: 0 0 6px 6px;
          min-height: ${editorHeight}px;
          font-size: 14px;
          font-family: inherit;
        }
        .markdown-editor-wrapper .ql-editor {
          min-height: ${editorHeight - 20}px;
          padding: 12px;
        }
        .markdown-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .markdown-editor-wrapper .ql-container:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }
        .markdown-editor-wrapper .ql-snow .ql-stroke {
          stroke: #374151;
        }
        .markdown-editor-wrapper .ql-snow .ql-fill {
          fill: #374151;
        }
        .markdown-editor-wrapper .ql-snow .ql-picker {
          color: #374151;
        }
      `}</style>
    </div>
  );
};

export default MarkdownQuillEditor;